/**
 * MemoryChaptersModal — host-facing surface for creating Memory
 * Chapters from the live wall.
 *
 * Two paths surface from the same modal:
 *
 *   1. Auto-curated suggestions — event-aware presets (school → "Last
 *      Day Memories"; wedding → "First Dance"; etc.). One tap composes.
 *
 *   2. Build your own ✦ — a multi-stage editorial builder so hosts can
 *      hand-pull the specific moments they want to preserve. Per spec
 *      the interaction must NOT feel like file-picking:
 *        • no checkboxes, no "selected: 3/15" counters, no blue states
 *        • tap a polaroid → it gently lifts → soft gold edge → glides
 *          onto the "Tonight's Chapter ✦" shelf
 *        • drag to reorder inside the shelf
 *        • a single optional cover line — "I didn't want Year 6 to end
 *          like this." — is rendered onto the printable PNG itself
 *
 * The composing + ready stages are shared between both paths so the
 * delivery beat ("Your Memory Chapter is ready ✦") reads the same
 * whether the chapter was auto-curated or hand-pulled.
 */

import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { X, DownloadSimple, Sparkle, FilePdf } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const absoluteMedia = (u) => (u && !u.startsWith('http') ? `${BACKEND_URL}${u}` : u);

const STAGE = {
  CHOOSE:       'choose',
  MANUAL_MOOD:  'manual_mood',
  MANUAL_BUILD: 'manual_build',
  COMPOSING:    'composing',
  READY:        'ready',
  ERROR:        'error',
};

// Mood presets surfaced inside the manual builder. The first three
// align with the auto-curation rules; "Build your own" lets the host
// name the chapter themselves and stays the quietest option.
const MOOD_PRESETS = [
  { key: 'the_speeches',    name: 'The speeches',    mood: 'When the room got quiet to listen.' },
  { key: 'the_dancefloor',  name: 'The dancefloor',  mood: 'When the lights went low and the room moved.' },
  { key: 'the_final_hour',  name: 'The final hour',  mood: 'The last memories before the lights came up.' },
  { key: 'build_your_own',  name: 'Build your own',  mood: null },
];

const MAX_SHELF = 15;
const MIN_SHELF = 3;

export const MemoryChaptersModal = ({ event, onClose }) => {
  const [stage, setStage] = useState(STAGE.CHOOSE);
  const [suggestions, setSuggestions] = useState([]);
  const [pickedSuggestion, setPickedSuggestion] = useState(null);
  const [error, setError] = useState(null);
  const [createdChapter, setCreatedChapter] = useState(null);
  const [outputFormat, setOutputFormat] = useState('both');

  // Manual builder state
  const [allSnaps, setAllSnaps] = useState([]);
  const [shelfIds, setShelfIds] = useState([]);  // ordered snap_ids
  const [moodPreset, setMoodPreset] = useState(null);
  const [customName, setCustomName] = useState('');
  const [hostNote, setHostNote] = useState('');
  const dragSrcRef = useRef(null);

  // Auto-suggestions for CHOOSE stage
  useEffect(() => {
    if (!event?.event_id) return;
    axios.get(`${API}/events/${event.event_id}/memory-chapters/suggestions`)
      .then((r) => setSuggestions(r.data?.suggestions || []))
      .catch(() => setSuggestions([]));
  }, [event?.event_id]);

  // Lazy-load the full wall when the host enters the manual flow
  useEffect(() => {
    if (stage !== STAGE.MANUAL_BUILD || !event?.event_id || allSnaps.length > 0) return;
    axios.get(`${API}/events/${event.event_id}/memory-snaps?limit=200`)
      .then((r) => setAllSnaps(r.data?.snaps || []))
      .catch(() => setAllSnaps([]));
  }, [stage, event?.event_id, allSnaps.length]);

  const createChapter = async (sugg) => {
    setStage(STAGE.COMPOSING);
    setPickedSuggestion(sugg);
    setError(null);
    try {
      const r = await axios.post(`${API}/events/${event.event_id}/memory-chapters`, {
        name: sugg.name,
        mood: sugg.mood,
        rule: sugg.rule,
        output_format: outputFormat,
      });
      setCreatedChapter(r.data);
      setStage(STAGE.READY);
    } catch (err) {
      setError(err.response?.data?.detail || 'Composition failed — try again.');
      setStage(STAGE.ERROR);
    }
  };

  const composeManual = async () => {
    if (shelfIds.length < MIN_SHELF) return;
    const chapterName = (moodPreset?.key === 'build_your_own'
      ? (customName.trim() || 'A night I want to keep')
      : moodPreset?.name) || 'A night I want to keep';
    const chapterMood = moodPreset?.mood || 'Hand-pulled from the wall.';
    const sugg = { name: chapterName, mood: chapterMood, rule: null };
    setStage(STAGE.COMPOSING);
    setPickedSuggestion(sugg);
    setError(null);
    try {
      const r = await axios.post(`${API}/events/${event.event_id}/memory-chapters`, {
        name: chapterName,
        mood: chapterMood,
        snap_ids: shelfIds,
        host_note: hostNote.trim() || null,
        output_format: outputFormat,
      });
      setCreatedChapter(r.data);
      setStage(STAGE.READY);
    } catch (err) {
      setError(err.response?.data?.detail || 'Composition failed — try again.');
      setStage(STAGE.ERROR);
    }
  };

  // ── Shelf operations ─────────────────────────────────────────
  const onShelfAdd = (snapId) => {
    setShelfIds((prev) => (prev.includes(snapId) || prev.length >= MAX_SHELF ? prev : [...prev, snapId]));
  };
  const onShelfRemove = (snapId) => setShelfIds((prev) => prev.filter((s) => s !== snapId));
  const onShelfReorder = (fromId, toId) => {
    setShelfIds((prev) => {
      const next = [...prev];
      const fi = next.indexOf(fromId);
      const ti = next.indexOf(toId);
      if (fi === -1 || ti === -1) return prev;
      next.splice(fi, 1);
      next.splice(ti, 0, fromId);
      return next;
    });
  };

  const goManualMood = () => {
    setMoodPreset(null);
    setCustomName('');
    setHostNote('');
    setShelfIds([]);
    setStage(STAGE.MANUAL_MOOD);
  };

  const moodChosen = (preset) => {
    setMoodPreset(preset);
    setStage(STAGE.MANUAL_BUILD);
  };

  const shelfSnaps = shelfIds.map((id) => allSnaps.find((s) => s.snap_id === id)).filter(Boolean);

  return (
    <div
      data-testid="memory-chapters-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10, 6, 3, 0.78)', backdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        width: 'min(720px, 100%)', maxHeight: '92vh', overflowY: 'auto',
        background: 'linear-gradient(180deg, #1c1108 0%, #120a05 100%)',
        border: '1px solid rgba(212,175,94,0.20)',
        borderRadius: 24,
        padding: '28px 28px 24px',
        boxShadow: '0 30px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(212,175,94,0.10)',
        color: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
          <div>
            <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.82)', margin: 0 }}>✦ Memory Chapter</p>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 26, fontWeight: 500, letterSpacing: '-0.01em', margin: '8px 0 0', color: '#FFE7A8' }}>
              {stage === STAGE.MANUAL_MOOD && 'Choose the mood'}
              {stage === STAGE.MANUAL_BUILD && 'Pull memories into the chapter'}
              {(stage !== STAGE.MANUAL_MOOD && stage !== STAGE.MANUAL_BUILD) && 'Create a Memory Chapter'}
            </h2>
            <p style={{ margin: '6px 0 0', fontStyle: 'italic', fontSize: 13, color: 'rgba(255,235,210,0.62)' }}>
              {stage === STAGE.MANUAL_MOOD && 'What kind of night was this?'}
              {stage === STAGE.MANUAL_BUILD && 'Tap a memory to lift it onto the shelf.'}
              {(stage !== STAGE.MANUAL_MOOD && stage !== STAGE.MANUAL_BUILD) && "Curated keepsakes from tonight's wall."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="chapters-modal-close"
            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer' }}
          ><X size={16} weight="bold" /></button>
        </div>

        {stage === STAGE.CHOOSE && (
          <ChooseStage
            suggestions={suggestions}
            onPick={createChapter}
            onManual={goManualMood}
            outputFormat={outputFormat}
            setOutputFormat={setOutputFormat}
          />
        )}

        {stage === STAGE.MANUAL_MOOD && (
          <MoodStage
            onPick={moodChosen}
            onBack={() => setStage(STAGE.CHOOSE)}
            customName={customName}
            setCustomName={setCustomName}
          />
        )}

        {stage === STAGE.MANUAL_BUILD && (
          <BuildStage
            allSnaps={allSnaps}
            shelfIds={shelfIds}
            shelfSnaps={shelfSnaps}
            moodPreset={moodPreset}
            customName={customName}
            setCustomName={setCustomName}
            hostNote={hostNote}
            setHostNote={setHostNote}
            onAdd={onShelfAdd}
            onRemove={onShelfRemove}
            onReorder={onShelfReorder}
            onBack={() => setStage(STAGE.MANUAL_MOOD)}
            onCompose={composeManual}
            dragSrcRef={dragSrcRef}
          />
        )}

        {stage === STAGE.COMPOSING && <ComposingStage pickedSuggestion={pickedSuggestion} />}

        {stage === STAGE.READY && createdChapter && (
          <ReadyStage
            createdChapter={createdChapter}
            onAnother={() => { setCreatedChapter(null); setStage(STAGE.CHOOSE); setPickedSuggestion(null); }}
          />
        )}

        {stage === STAGE.ERROR && (
          <div data-testid="chapters-error-stage" style={{ padding: '32px 4px', textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: '#FF9F8E', margin: '0 0 12px' }}>{error}</p>
            <button
              type="button"
              onClick={() => { setError(null); setStage(STAGE.CHOOSE); }}
              data-testid="chapters-error-retry"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 44, padding: '0 18px', borderRadius: 999, background: 'rgba(212,175,94,0.18)', border: '1px solid rgba(212,175,94,0.4)', color: '#FFE7A8', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >Try again</button>
          </div>
        )}
      </div>
    </div>
  );
};


// ─── Stage components ─────────────────────────────────────────

const ChooseStage = ({ suggestions, onPick, onManual, outputFormat, setOutputFormat }) => (
  <div data-testid="chapters-choose-stage">
    {suggestions.length === 0 && (
      <div style={{ padding: '28px 0', textAlign: 'center', color: 'rgba(255,235,210,0.6)', fontSize: 13 }}>
        Not enough memories on the wall yet — chapters appear once 3+ snaps have been pinned.
      </div>
    )}

    {suggestions.map((s) => (
      <button
        key={s.rule}
        type="button"
        onClick={() => onPick(s)}
        data-testid={`chapter-suggestion-${s.rule}`}
        style={{
          width: '100%', textAlign: 'left',
          background: 'rgba(20,12,6,0.6)',
          border: '1px solid rgba(212,175,94,0.18)',
          borderRadius: 16,
          padding: '16px 16px 14px',
          marginBottom: 12,
          cursor: 'pointer',
          color: '#fff',
          transition: 'border-color 250ms ease-out, transform 250ms ease-out',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,94,0.48)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,94,0.18)'; e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <p style={{ margin: 0, fontFamily: 'Georgia, serif', fontSize: 19, color: '#FFE7A8', fontWeight: 500, letterSpacing: '-0.01em' }}>
              {s.name} <span style={{ color: 'rgba(212,175,94,0.78)', fontSize: 14 }}>✦</span>
            </p>
            <p style={{ margin: '4px 0 0', fontStyle: 'italic', fontSize: 13, color: 'rgba(255,235,210,0.65)' }}>{s.mood}</p>
          </div>
          <span style={{ flex: '0 0 auto', fontStyle: 'italic', fontSize: 11, color: 'rgba(255,235,210,0.5)', letterSpacing: '0.01em' }}>
            {Math.min(s.snap_count, 15)} moments gathered
          </span>
        </div>
        <div style={{ display: 'flex', gap: 0, marginTop: 14, paddingLeft: 6, height: 80 }}>
          {(s.preview || []).slice(0, 4).map((p, i) => {
            const rotations = [-6, 4, -2, 7];
            const lifts     = [0, -4, 0, -8];
            const overlaps  = [0, -12, -8, -14];
            const shadows   = [
              '0 5px 11px rgba(0,0,0,0.42), 0 1px 2px rgba(0,0,0,0.4)',
              '0 7px 14px rgba(0,0,0,0.5),  0 2px 3px rgba(0,0,0,0.5)',
              '0 4px 10px rgba(0,0,0,0.38), 0 1px 2px rgba(0,0,0,0.36)',
              '0 8px 16px rgba(0,0,0,0.52), 0 2px 4px rgba(0,0,0,0.5)',
            ];
            return (
              <div key={p.snap_id} style={{
                width: 56, height: 70, borderRadius: 2,
                background: '#FCFCF7', padding: 3,
                marginLeft: overlaps[i] || 0,
                marginTop: lifts[i] || 0,
                transform: `rotate(${rotations[i]}deg)`,
                boxShadow: shadows[i],
                zIndex: 4 - i, flex: '0 0 auto',
                transition: 'transform 250ms ease-out',
              }}>
                <img src={absoluteMedia(p.thumbnail_url || p.photo_url)} alt="" style={{ width: '100%', height: 44, objectFit: 'cover', display: 'block' }} />
              </div>
            );
          })}
        </div>
      </button>
    ))}

    {/* Manual builder entry — quiet, set apart from the auto-curation
        suggestions so it reads as an intentional alternative path. */}
    {suggestions.length > 0 && (
      <button
        type="button"
        onClick={onManual}
        data-testid="chapter-suggestion-build-your-own"
        style={{
          width: '100%', textAlign: 'left',
          background: 'transparent',
          border: '1px dashed rgba(212,175,94,0.30)',
          borderRadius: 16,
          padding: '16px 18px',
          marginTop: 6, marginBottom: 4,
          cursor: 'pointer',
          color: '#fff',
          transition: 'border-color 250ms ease-out, background 250ms ease-out',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,94,0.55)'; e.currentTarget.style.background = 'rgba(20,12,6,0.4)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,94,0.30)'; e.currentTarget.style.background = 'transparent'; }}
      >
        <p style={{ margin: 0, fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 17, color: '#FFE7A8', letterSpacing: '-0.005em' }}>
          Build your own <span style={{ color: 'rgba(212,175,94,0.78)' }}>✦</span>
        </p>
        <p style={{ margin: '4px 0 0', fontStyle: 'italic', fontSize: 12.5, color: 'rgba(255,235,210,0.55)' }}>
          Hand-pull the moments you want to preserve.
        </p>
      </button>
    )}

    {suggestions.length > 0 && (
      <div style={{ marginTop: 18, padding: '14px 0 0', borderTop: '1px solid rgba(255,235,210,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 11, color: 'rgba(255,235,210,0.5)', letterSpacing: '0.06em' }}>Output</span>
        <div role="tablist" style={{ display: 'inline-flex', gap: 4, padding: 3, background: 'rgba(255,255,255,0.04)', borderRadius: 999 }}>
          {[
            { v: 'png',  label: 'For sharing' },
            { v: 'both', label: 'For sharing + print' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setOutputFormat(opt.v)}
              data-testid={`chapters-format-${opt.v}`}
              style={{
                height: 30, padding: '0 14px', borderRadius: 999,
                background: outputFormat === opt.v ? 'rgba(212,175,94,0.20)' : 'transparent',
                border: outputFormat === opt.v ? '1px solid rgba(212,175,94,0.42)' : '1px solid transparent',
                color: outputFormat === opt.v ? '#FFE7A8' : 'rgba(255,235,210,0.55)',
                fontSize: 11, fontWeight: 700, letterSpacing: '0.02em', cursor: 'pointer',
              }}
            >{opt.label}</button>
          ))}
        </div>
      </div>
    )}
  </div>
);


const MoodStage = ({ onPick, onBack, customName, setCustomName }) => (
  <div data-testid="chapters-mood-stage">
    {MOOD_PRESETS.map((p) => (
      <button
        key={p.key}
        type="button"
        onClick={() => onPick(p)}
        data-testid={`chapter-mood-${p.key}`}
        style={{
          width: '100%', textAlign: 'left',
          background: p.key === 'build_your_own' ? 'transparent' : 'rgba(20,12,6,0.6)',
          border: p.key === 'build_your_own'
            ? '1px dashed rgba(212,175,94,0.32)'
            : '1px solid rgba(212,175,94,0.18)',
          borderRadius: 16, padding: '14px 18px', marginBottom: 10,
          cursor: 'pointer', color: '#fff',
          transition: 'border-color 250ms ease-out, transform 250ms ease-out',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,94,0.55)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = p.key === 'build_your_own' ? 'rgba(212,175,94,0.32)' : 'rgba(212,175,94,0.18)'; e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        <p style={{ margin: 0, fontFamily: 'Georgia, serif', fontSize: 19, color: '#FFE7A8', letterSpacing: '-0.005em' }}>
          {p.name} <span style={{ color: 'rgba(212,175,94,0.78)', fontSize: 14 }}>✦</span>
        </p>
        {p.mood && (
          <p style={{ margin: '4px 0 0', fontStyle: 'italic', fontSize: 13, color: 'rgba(255,235,210,0.62)' }}>{p.mood}</p>
        )}
        {!p.mood && (
          <p style={{ margin: '4px 0 0', fontStyle: 'italic', fontSize: 13, color: 'rgba(255,235,210,0.55)' }}>
            Name it yourself in the next step.
          </p>
        )}
      </button>
    ))}
    <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-start' }}>
      <button
        type="button"
        onClick={onBack}
        data-testid="chapter-mood-back"
        style={{
          background: 'transparent', border: 'none',
          color: 'rgba(255,235,210,0.5)', fontStyle: 'italic',
          fontFamily: 'Georgia, serif', fontSize: 13, cursor: 'pointer',
        }}
      >← Back</button>
    </div>
  </div>
);


const ShelfPolaroid = ({ snap, onClick, lifted, dragHandlers, testid }) => (
  <div
    data-testid={testid}
    onClick={onClick}
    {...(dragHandlers || {})}
    style={{
      width: 96, height: 122, padding: 6,
      background: '#FAF6E8',
      borderRadius: 2,
      boxShadow: lifted
        ? '0 12px 28px rgba(0,0,0,0.55), 0 0 0 1.5px rgba(212,175,94,0.65)'
        : '0 4px 12px rgba(0,0,0,0.42)',
      transform: lifted ? 'translateY(-4px) rotate(-2deg)' : 'rotate(0deg)',
      transition: 'transform 280ms ease, box-shadow 280ms ease',
      cursor: 'pointer',
      flex: '0 0 auto',
      userSelect: 'none',
    }}
  >
    <img
      src={absoluteMedia(snap.photo_url)}
      alt=""
      draggable={false}
      style={{ width: '100%', height: 84, objectFit: 'cover', display: 'block' }}
    />
    <p style={{
      margin: '6px 0 0', fontFamily: 'Caveat, Georgia, cursive',
      fontSize: 11, color: '#3a230f', lineHeight: 1.1,
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    }}>{(snap.message || '').slice(0, 16) || ' '}</p>
  </div>
);


const BuildStage = ({
  allSnaps, shelfIds, shelfSnaps, moodPreset,
  customName, setCustomName, hostNote, setHostNote,
  onAdd, onRemove, onReorder, onBack, onCompose, dragSrcRef,
}) => {
  const remaining = allSnaps.filter((s) => !shelfIds.includes(s.snap_id));
  const canCompose = shelfIds.length >= MIN_SHELF;

  // HTML5 drag handlers wired only on shelf items.
  const dragHandlers = (snapId) => ({
    draggable: true,
    onDragStart: (e) => { dragSrcRef.current = snapId; e.dataTransfer.effectAllowed = 'move'; },
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
    onDrop: (e) => {
      e.preventDefault();
      const src = dragSrcRef.current;
      if (src && src !== snapId) onReorder(src, snapId);
      dragSrcRef.current = null;
    },
    onDragEnd: () => { dragSrcRef.current = null; },
  });

  return (
    <div data-testid="chapters-build-stage">
      {/* Shelf — Tonight's Chapter ✦. Always rendered, even when
          empty, so the host sees the destination from the first tap. */}
      <div style={{
        background: 'linear-gradient(180deg, rgba(40,24,12,0.55) 0%, rgba(20,12,6,0.62) 100%)',
        border: '1px solid rgba(212,175,94,0.30)',
        borderRadius: 14, padding: '14px 14px 16px', marginBottom: 16,
        minHeight: 168,
      }} data-testid="chapter-shelf">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <p style={{ margin: 0, fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 14, color: '#FFE7A8' }}>
            Tonight's chapter <span style={{ color: 'rgba(212,175,94,0.85)' }}>✦</span>
            {moodPreset && (
              <span style={{ marginLeft: 10, fontStyle: 'normal', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.7)' }}>
                {moodPreset.name}
              </span>
            )}
          </p>
          <span style={{ fontStyle: 'italic', fontSize: 12, color: 'rgba(255,235,210,0.55)' }}>
            {shelfIds.length === 0 ? 'Empty for now' : `${shelfIds.length} moment${shelfIds.length === 1 ? '' : 's'} on the shelf`}
          </span>
        </div>
        {shelfSnaps.length === 0 ? (
          <p style={{ margin: '24px 8px', fontStyle: 'italic', fontSize: 13, color: 'rgba(255,235,210,0.45)' }}>
            Tap a polaroid below to lift it onto the shelf.
          </p>
        ) : (
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {shelfSnaps.map((s) => (
              <ShelfPolaroid
                key={s.snap_id}
                snap={s}
                lifted={true}
                onClick={() => onRemove(s.snap_id)}
                dragHandlers={dragHandlers(s.snap_id)}
                testid={`chapter-shelf-snap-${s.snap_id}`}
              />
            ))}
          </div>
        )}

        {/* Optional custom name (only for "Build your own") */}
        {moodPreset?.key === 'build_your_own' && (
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.7)', marginBottom: 6 }}>
              Name this chapter
            </label>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value.slice(0, 60))}
              placeholder="A night I want to keep"
              data-testid="chapter-custom-name"
              style={{
                width: '100%', padding: '10px 12px',
                background: 'rgba(20,12,6,0.7)',
                border: '1px solid rgba(212,175,94,0.30)',
                borderRadius: 8,
                fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 15,
                color: '#FFE7A8', outline: 'none',
              }}
            />
          </div>
        )}

        {/* Optional cover quote / host inscription — one single line,
            rendered onto the printable PNG itself. */}
        <div style={{ marginTop: 14 }}>
          <label style={{ display: 'block', fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.7)', marginBottom: 6 }}>
            A line for the cover <span style={{ textTransform: 'none', letterSpacing: 0, fontStyle: 'italic', color: 'rgba(255,235,210,0.45)' }}>(optional)</span>
          </label>
          <input
            type="text"
            value={hostNote}
            onChange={(e) => setHostNote(e.target.value.slice(0, 80))}
            placeholder="I didn't want Year 6 to end like this."
            data-testid="chapter-host-note"
            style={{
              width: '100%', padding: '10px 12px',
              background: 'rgba(20,12,6,0.7)',
              border: '1px solid rgba(212,175,94,0.22)',
              borderRadius: 8,
              fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 15,
              color: '#FFE7A8', outline: 'none',
            }}
          />
          <p style={{ margin: '6px 2px 0', fontStyle: 'italic', fontSize: 11.5, color: 'rgba(255,235,210,0.45)' }}>
            One line. Printed beneath the title.
          </p>
        </div>
      </div>

      {/* Wall — remaining polaroids the host can lift onto the shelf. */}
      <div data-testid="chapter-wall-source">
        <p style={{ margin: '0 0 10px', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.62)' }}>
          On the wall
        </p>
        {allSnaps.length === 0 && (
          <p style={{ fontStyle: 'italic', color: 'rgba(255,235,210,0.5)', fontSize: 13, padding: '20px 6px' }}>
            Loading memories…
          </p>
        )}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 12,
          maxHeight: 260, overflowY: 'auto',
          padding: '6px 2px 14px',
        }}>
          {remaining.map((s) => (
            <ShelfPolaroid
              key={s.snap_id}
              snap={s}
              lifted={false}
              onClick={() => onAdd(s.snap_id)}
              testid={`chapter-wall-snap-${s.snap_id}`}
            />
          ))}
          {remaining.length === 0 && allSnaps.length > 0 && (
            <p style={{ fontStyle: 'italic', color: 'rgba(255,235,210,0.4)', fontSize: 12.5, padding: '12px 6px' }}>
              Every memory is on the shelf.
            </p>
          )}
        </div>
      </div>

      {/* Bottom action row */}
      <div style={{ marginTop: 18, padding: '14px 0 0', borderTop: '1px solid rgba(255,235,210,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <button
          type="button"
          onClick={onBack}
          data-testid="chapter-build-back"
          style={{
            background: 'transparent', border: 'none',
            color: 'rgba(255,235,210,0.5)', fontStyle: 'italic',
            fontFamily: 'Georgia, serif', fontSize: 13, cursor: 'pointer',
          }}
        >← Choose a different mood</button>

        <button
          type="button"
          onClick={onCompose}
          disabled={!canCompose}
          data-testid="chapter-compose-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            height: 44, padding: '0 22px', borderRadius: 999,
            background: canCompose
              ? 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)'
              : 'rgba(212,175,94,0.18)',
            color: canCompose ? '#0F0B1A' : 'rgba(255,235,210,0.45)',
            border: 'none',
            fontFamily: 'Georgia, serif',
            fontWeight: 700, fontSize: 13.5, letterSpacing: '0.01em',
            cursor: canCompose ? 'pointer' : 'not-allowed',
            transition: 'transform 240ms ease',
          }}
          onMouseEnter={(e) => { if (canCompose) e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          {canCompose ? 'Compose this chapter ✦' : `${MIN_SHELF - shelfIds.length} more to compose`}
        </button>
      </div>
    </div>
  );
};


const ComposingStage = ({ pickedSuggestion }) => (
  <div data-testid="chapters-composing-stage" style={{ padding: '48px 8px 40px', textAlign: 'center' }}>
    <Sparkle size={42} weight="duotone" color="#D4AF5E" style={{ animation: 'chaptersPulse 2.4s ease-in-out infinite', opacity: 0.85 }} />
    <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.75)', margin: '20px 0 16px' }}>
      ✦ Preparing
    </p>
    <div
      data-testid="chapters-composing-title"
      style={{
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'italic', fontSize: 28, color: '#FFE7A8',
        letterSpacing: '-0.005em', display: 'inline-block',
        position: 'relative', overflow: 'hidden', whiteSpace: 'nowrap',
        maxWidth: '100%', borderRight: '2px solid rgba(212,175,94,0.85)',
        width: `${(pickedSuggestion?.name || '').length + 0.5}ch`,
        animation: `chaptersType ${Math.max(900, ((pickedSuggestion?.name || '').length) * 70)}ms steps(${Math.max(1, (pickedSuggestion?.name || '').length)}) forwards, chaptersCaret 0.85s steps(1) infinite`,
      }}
    >{pickedSuggestion?.name || 'Memory Chapter'}</div>
    <p style={{ fontStyle: 'italic', fontSize: 13, color: 'rgba(255,235,210,0.55)', margin: '18px 0 4px' }}>
      {pickedSuggestion?.mood || 'Curating the right memories…'}
    </p>
    <p style={{ fontSize: 11, color: 'rgba(255,235,210,0.42)', marginTop: 10 }}>
      This takes a few seconds.
    </p>
    <style>{`
      @keyframes chaptersPulse { 0%,100% { opacity: 0.4; transform: scale(0.96); } 50% { opacity: 1; transform: scale(1.05); } }
      @keyframes chaptersType { from { width: 0; } }
      @keyframes chaptersCaret { 0%, 50% { border-right-color: rgba(212,175,94,0.85); } 51%, 100% { border-right-color: transparent; } }
    `}</style>
  </div>
);


const ReadyStage = ({ createdChapter, onAnother }) => (
  <div data-testid="chapters-ready-stage">
    <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.82)', margin: '0 0 8px' }}>✦ Prepared</p>
    <h3 style={{ fontFamily: 'Georgia, serif', fontSize: 26, fontWeight: 500, letterSpacing: '-0.01em', margin: '0 0 4px', color: '#FFE7A8' }}>
      Your Memory Chapter is ready <span style={{ color: 'rgba(212,175,94,0.85)', fontSize: 18 }}>✦</span>
    </h3>
    <p style={{ fontStyle: 'italic', fontSize: 13, color: 'rgba(255,235,210,0.62)', margin: '0 0 16px' }}>
      "{createdChapter.name}" — {createdChapter.mood?.toLowerCase() || 'a curated keepsake from the night'}.
    </p>
    <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 18, background: '#1a0f08', border: '1px solid rgba(212,175,94,0.20)', boxShadow: '0 18px 40px rgba(0,0,0,0.45)' }}>
      <img
        src={absoluteMedia(createdChapter.png_url)}
        alt={createdChapter.name}
        style={{ width: '100%', display: 'block' }}
        data-testid="chapters-preview-image"
      />
    </div>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      <a
        href={absoluteMedia(createdChapter.png_url)}
        download={`${(createdChapter.name || 'chapter').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`}
        data-testid="chapters-download-png"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 48, padding: '0 18px', borderRadius: 999, background: 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)', color: '#0F0B1A', textDecoration: 'none', fontWeight: 800, fontSize: 13 }}
      ><DownloadSimple size={16} weight="bold" /> Save for sharing</a>
      {createdChapter.pdf_url && (
        <a
          href={absoluteMedia(createdChapter.pdf_url)}
          download={`${(createdChapter.name || 'chapter').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`}
          data-testid="chapters-download-pdf"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 48, padding: '0 18px', borderRadius: 999, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(212,175,94,0.42)', color: '#FFE7A8', textDecoration: 'none', fontWeight: 700, fontSize: 13 }}
        ><FilePdf size={16} weight="bold" /> Save for print</a>
      )}
      <button
        type="button"
        onClick={onAnother}
        data-testid="chapters-make-another"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 48, padding: '0 18px', borderRadius: 999, background: 'transparent', border: '1px solid rgba(255,255,255,0.16)', color: 'rgba(255,235,210,0.85)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
      >Prepare another</button>
    </div>
  </div>
);

export default MemoryChaptersModal;
